"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.upcomingEventSchema = exports.upcomingEventMasterSchema = exports.upcomingEventsQuerySchema = exports.updatePeriodicBatchSlotResponseSchema = exports.updatePeriodicBatchSlotItemSchema = exports.updatePeriodicBatchSlotInputSchema = exports.deleteBatchSlotResponseSchema = exports.deleteBatchSlotInputSchema = exports.createPeriodicBatchSlotsInputSchema = exports.activeSlotsOutputSchema = exports.activeSlotItemSchema = exports.getMasterSlotsResponseSchema = exports.scheduleSlotSchema = exports.periodicSlotConfigSchema = exports.slotStatusSchema = exports.Period = exports.SlotStatus = void 0;
const zod_1 = require("zod");
const user_1 = require("./user");
var SlotStatus;
(function (SlotStatus) {
    SlotStatus["Free"] = "free";
    SlotStatus["Booked"] = "booked";
    SlotStatus["Reserved"] = "reserved";
    SlotStatus["Paid"] = "paid";
})(SlotStatus || (exports.SlotStatus = SlotStatus = {}));
var Period;
(function (Period) {
    Period["Daily"] = "daily";
    Period["Weekly"] = "weekly";
    Period["Monthly"] = "monthly";
})(Period || (exports.Period = Period = {}));
exports.slotStatusSchema = zod_1.z.nativeEnum(SlotStatus);
/** Bulk-create metadata saved on `schedule_slots.periodicSlotConfig` */
exports.periodicSlotConfigSchema = zod_1.z.object({
    id: zod_1.z.number().int().positive(),
    chunkSizeMinutes: zod_1.z.number().int().positive(),
    period: zod_1.z.nativeEnum(Period),
    repeatCount: zod_1.z.number().int().positive(),
});
exports.scheduleSlotSchema = zod_1.z.object({
    id: zod_1.z.number().int().positive(),
    master: user_1.userSchemaBase,
    startTime: zod_1.z.coerce.date(),
    endTime: zod_1.z.coerce.date(),
    status: exports.slotStatusSchema.default(SlotStatus.Free),
    title: zod_1.z.string().nullable(),
    youtubeId: zod_1.z.string().nullable(),
    reservedBy: user_1.userSchemaBase.nullish(),
    price: zod_1.z.number().nullish(),
    /** Chunk position within one occurrence of the interval (0-based; resets each repeat). */
    chunkIndex: zod_1.z.number().int().min(0).nullish(),
    periodicSlotConfig: exports.periodicSlotConfigSchema.nullish(),
});
/** GET /schedule/slot/user/:userId — full slots for master calendar */
exports.getMasterSlotsResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    slots: zod_1.z.array(exports.scheduleSlotSchema),
});
/** Single slot returned by GET /schedule/slot/user/:userId/active */
exports.activeSlotItemSchema = zod_1.z.object({
    id: zod_1.z.number().int().positive(),
    startTime: zod_1.z.coerce.date(),
    endTime: zod_1.z.coerce.date(),
    status: exports.slotStatusSchema,
    title: zod_1.z.string().nullable(),
    price: zod_1.z.number().nullable(),
});
/** Response body for GET /schedule/slot/user/:userId/active */
exports.activeSlotsOutputSchema = zod_1.z.object({
    success: zod_1.z.boolean().default(true),
    slots: zod_1.z.array(exports.activeSlotItemSchema),
});
/** POST /schedule/slot/create-periodic-batch-slots */
exports.createPeriodicBatchSlotsInputSchema = zod_1.z
    .object({
    interval: zod_1.z.object({
        start: zod_1.z.coerce.date(),
        end: zod_1.z.coerce.date(),
    }),
    chunkSizeMinutes: zod_1.z.number().positive().default(60),
    period: zod_1.z.nativeEnum(Period).default(Period.Weekly),
    repeatCount: zod_1.z.number().int().positive().default(50),
    title: zod_1.z.string().max(2000).nullable().optional(),
    description: zod_1.z.string().max(10000).nullable().optional(),
    price: zod_1.z.number().nonnegative().nullable().optional(),
    youtubeId: zod_1.z.string().max(100).nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.interval.start >= value.interval.end) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ["interval", "start"],
            message: "interval.start must be before interval.end",
        });
    }
});
/** POST /schedule/slot/delete-batch */
exports.deleteBatchSlotInputSchema = zod_1.z.object({
    slotId: zod_1.z.number().int().positive(),
});
exports.deleteBatchSlotResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean().default(true),
    deletedIds: zod_1.z.array(zod_1.z.number().int().positive()),
    deletedCount: zod_1.z.number().int().nonnegative(),
});
/** POST /schedule/slot/update-periodic-batch-slots */
exports.updatePeriodicBatchSlotInputSchema = zod_1.z
    .object({
    slotId: zod_1.z.number().int().positive(),
    startTime: zod_1.z.coerce.date().optional(),
    endTime: zod_1.z.coerce.date().optional(),
    title: zod_1.z.string().nullable().optional(),
    youtubeId: zod_1.z.string().nullable().optional(),
    price: zod_1.z.number().nullable().optional(),
})
    .superRefine((data, ctx) => {
    const hasUpdate = data.startTime !== undefined ||
        data.endTime !== undefined ||
        data.title !== undefined ||
        data.youtubeId !== undefined ||
        data.price !== undefined;
    if (!hasUpdate) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: "At least one field to update is required",
        });
    }
    const hasStart = data.startTime !== undefined;
    const hasEnd = data.endTime !== undefined;
    if (hasStart !== hasEnd) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: hasStart ? ["endTime"] : ["startTime"],
            message: "startTime and endTime must both be provided when updating times",
        });
    }
});
/** Response slot shape — omits user relations. */
exports.updatePeriodicBatchSlotItemSchema = exports.scheduleSlotSchema.omit({
    master: true,
    reservedBy: true,
});
exports.updatePeriodicBatchSlotResponseSchema = zod_1.z.object({
    success: zod_1.z.boolean().default(true),
    updatedCount: zod_1.z.number().int().nonnegative(),
    slots: zod_1.z.array(exports.updatePeriodicBatchSlotItemSchema),
});
/** GET /schedule/upcoming-events */
exports.upcomingEventsQuerySchema = zod_1.z.object({
    from: zod_1.z.coerce.date().optional(),
    to: zod_1.z.coerce.date().optional(),
    limit: zod_1.z.coerce.number().int().positive().optional(),
});
exports.upcomingEventMasterSchema = user_1.userSchemaBase.pick({
    id: true,
    username: true,
    title: true,
    profilePictureThumbnailUrl: true,
    languages: true,
});
exports.upcomingEventSchema = exports.scheduleSlotSchema
    .omit({ reservedBy: true, periodicSlotConfig: true })
    .extend({
    master: exports.upcomingEventMasterSchema,
});
